import { asCategoryId, asItemId, asTodoId, daysBetween, newId } from "@pack-it-up/shared";
import { emptyCompletion } from "./completion.js";
import { resolve } from "./relativeOffset.js";
// Assignments carry over verbatim, `specific` included — the row is copied with its
// `assignment` untouched. This is deliberately *not* the sync-back rule (Doc 13 §4.1,
// which skips `specific`): a copy reproduces the source list as it stands, and a
// `specific` assignment is part of that structure.
//
// For a trip → trip copy the people come along with them: `copyTrip` hands the source's
// `members` map to `createTrip` (which still makes the caller the owner), so the
// allocations resolve in the copy. Templates *should* carry participants the same way —
// a "Child airplane trip" template naming the child, whose import adds them to the trip
// — but they don't yet (issue #185), so for now an allocation copied into or out of a
// template names someone the target has no member entry for, and that row reads as
// permanently incomplete (`requiredCount` counts the allocatee, nobody can check it)
// until they are added. Keeping the ids is what lets #185 resolve them later.
// Doc 07 §4 — the offset is the durable value and the due date only its resolved
// cache, so a copy travels as an offset and re-resolves at the far end:
//
//   read    keep the todo's offset; failing that, derive one from its due date when the
//           source is a trip with a start date to measure against
//   write   a template stores the offset alone (it has no start date); a trip resolves
//           it against *its own* start date
//
// That is what makes a copied trip a fresh run rather than a stale one: "renew the
// passport a week before" follows the copy to its new dates instead of pointing at
// last year's. Nothing carries a manual pin across — the pin was a decision about the
// source list's dates.
const copyDueDate = (todo, { source, target }) => {
    const offset = todo.relativeDueOffset ??
        (source.kind === "trip" && source.startDate != null && todo.dueDate != null
            ? { value: daysBetween(source.startDate, todo.dueDate), unit: "day" }
            : undefined);
    if (target.kind === "template") {
        return { ...(offset ? { relativeDueOffset: offset } : {}), dueDateManuallyOverridden: false };
    }
    // No start date on the new trip yet: the offset waits, and `recomputeTodoDueDates`
    // resolves it the moment one is set (Doc 07 §4).
    const due = offset !== undefined && target.startDate != null ? resolve(target.startDate, offset) : undefined;
    return {
        ...(due !== undefined ? { dueDate: due } : {}),
        ...(offset ? { relativeDueOffset: offset } : {}),
        dueDateManuallyOverridden: false,
    };
};
// Provenance survives only a trip → trip copy, where it stays meaningful: the copy
// was imported from the same templates, so "reset to import" and sync-back keep
// working. A template target drops it — a template row is a source, never an import —
// and so does template → trip, since the copy is a fresh list, not an import of the
// template it came from.
const keepsProvenance = (opts) => opts.source.kind === "trip" && opts.target.kind === "trip";
// Staging survives only a template → template copy (issue #188). On a trip it is
// progress — who has put what in the pile for *this* run — and a copy is a fresh run,
// so a trip source drops it exactly as it drops completions. On a template it is
// content: "this bin of supplies is already packed" is part of what the template says,
// and a duplicate that lost it would not be the same template. A duplicate carries the
// participants across under the same ids (issue #185), so the copied entries still name
// the travelers they were recorded against.
//
// A template → trip copy would be the one ambiguous case; there isn't one. Bringing a
// template's staging onto a trip is the import's job, which re-keys the entries onto the
// members its participants resolved to (`importTemplates`).
const keepsStaging = (opts) => opts.source.kind === "template" && opts.target.kind === "template";
export const planCopy = (contents, opts) => {
    const gen = opts.idGen ?? newId;
    const provenance = keepsProvenance(opts);
    const staging = keepsStaging(opts);
    // Source category id → its copy's id. Rows whose category didn't come along (a
    // dangling categoryId) are dropped rather than orphaned.
    const categoryIds = new Map();
    const categoryCreates = [];
    for (const c of contents.categories) {
        const id = asCategoryId(gen());
        categoryIds.set(c.id, id);
        categoryCreates.push({
            id,
            parentId: opts.target.id,
            name: c.name,
            order: c.order,
            // Collapse is per-person view state (Issue #240); a copy starts expanded for everyone.
            collapsedBy: {},
            ...(c.emoji !== undefined ? { emoji: c.emoji } : {}),
            ...(c.kind !== undefined ? { kind: c.kind } : {}),
        });
    }
    const itemCreates = [];
    for (const i of contents.items) {
        const categoryId = categoryIds.get(i.categoryId);
        if (categoryId === undefined)
            continue;
        itemCreates.push({
            id: asItemId(gen()),
            categoryId,
            name: i.name,
            quantity: i.quantity,
            ...(i.description !== undefined ? { description: i.description } : {}),
            ...(i.notes !== undefined ? { notes: i.notes } : {}),
            ...(i.tags !== undefined ? { tags: [...i.tags] } : {}),
            assignment: i.assignment,
            // Who packed what is about the source list, never the copy. Staging is different:
            // on a template it is content, and a duplicate keeps it (see `keepsStaging`).
            completions: emptyCompletion(),
            ...(staging && i.staging !== undefined ? { staging: { byParticipant: { ...i.staging.byParticipant } } } : {}),
            order: i.order,
            ...(provenance && i.provenance ? { provenance: i.provenance } : {}),
            ...(provenance && i.templateIds ? { templateIds: [...i.templateIds] } : {}),
        });
    }
    const todoCreates = [];
    for (const t of contents.todos) {
        const categoryId = categoryIds.get(t.categoryId);
        if (categoryId === undefined)
            continue;
        todoCreates.push({
            id: asTodoId(gen()),
            categoryId,
            name: t.name,
            ...(t.description !== undefined ? { description: t.description } : {}),
            ...(t.notes !== undefined ? { notes: t.notes } : {}),
            ...(t.tags !== undefined ? { tags: [...t.tags] } : {}),
            assignment: t.assignment,
            completions: emptyCompletion(),
            ...copyDueDate(t, opts),
            // Reminder bookkeeping belongs to the source list's schedule.
            remindersSentTo: {},
            reminderComplete: false,
            order: t.order,
            ...(provenance && t.provenance ? { provenance: t.provenance } : {}),
            ...(provenance && t.templateIds ? { templateIds: [...t.templateIds] } : {}),
        });
    }
    return { targetId: opts.target.id, categoryCreates, itemCreates, todoCreates };
};
// The default name for a copy: "Beach trip" → "Beach trip (copy)". Idempotent-ish by
// design — copying a copy gives "Beach trip (copy) (copy)", which is honest about what
// happened and is what the user edits in the dialog anyway.
export const copyName = (name) => `${name} (copy)`;
//# sourceMappingURL=copyList.js.map